import classNames from 'classnames';
import { FC, ReactEventHandler, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSendEventsMutation } from '../../api/queries/event';
import { useCompleteQuestionnaireMutation } from '../../api/queries/question';
import BackButton from '../../components/BackButton';
import { useAsyncError } from '../../components/Error/useAsyncError';
import { LoadingImages } from '../../components/LoadingImages/LoadingImages';
import { StepsUpdateContext } from '../../components/Steps/StepsContext';
import { StylesheetContext } from '../../contexts/StylesheetContext';
import { TicketContext } from '../../contexts/TicketContext';
import { SelfServiceError } from '../../helpers/Errors';
import { useScrollToTop } from '../../helpers/hooks/useScrollToTop';
import { isInteger } from '../../helpers/isInteger';
import { isSalesforceMessage } from '../../helpers/Message';
import { Ticket } from '../../interfaces/Ticket';
import { SupportedErrorTypes } from '../../services/ErrorService';
import usePageTitle from '../../services/hooks/usePageTitle';
import style from './Questions.module.scss';
import QuestionsController from './QuestionsController';

const Questions: FC = () => {
  const { stylesheet, globalStylesheet } = useContext(StylesheetContext);
  const { ticket, fetchTicket } = useContext(TicketContext);
  const { completeCurrentStep, navigateToNextStep } = useContext(StepsUpdateContext);
  const [iframeHeight, setIframeHeight] = useState(0);
  const [isLoading, setLoading] = useState(true);
  const { completeQuestionnaire, isPending } = useCompleteQuestionnaireMutation();
  const { sendEvents } = useSendEventsMutation();
  const iframeFailedToLoadTimeout = useRef<NodeJS.Timeout | null>(null);
  const lastHeightRef = useRef<number>(0);

  const { throwAsyncError } = useAsyncError();

  const { t } = useTranslation();

  const url = QuestionsController.getQuestionsUrl({
    stylesheet,
    globalStylesheet,
    ticket
  });

  usePageTitle(t('pageTitle:questions'));

  const { scrollTop } = useScrollToTop();

  const navigateRef = useRef<() => void>(undefined);

  useEffect(() => {
    navigateRef.current = () => {
      completeCurrentStep();
      setTimeout(() => {
        // Delay the naviation to ensure that the pageguard use effect doesnt fire asynchronously (race condition)
        navigateToNextStep();
      }, 100);
    };
  }, [completeCurrentStep, navigateToNextStep]);

  useEffect(() => {
    if (url === '') {
      navigateRef.current?.();
    }
  }, [url]);

  const cleanupIframeFailedToLoadTimeout = useCallback(() => {
    if (iframeFailedToLoadTimeout.current) {
      clearTimeout(iframeFailedToLoadTimeout.current);
      iframeFailedToLoadTimeout.current = null;
    }
  }, []);

  useEffect(() => {
    return cleanupIframeFailedToLoadTimeout;
  }, [cleanupIframeFailedToLoadTimeout]);

  const initializeIframe = useCallback((height: number) => {
    const HEIGHT_BUFFER = 30;

    if (height <= 10) {
      return;
    }

    const nextHeight = height + HEIGHT_BUFFER;

    if (nextHeight === lastHeightRef.current) return;
    lastHeightRef.current = nextHeight;

    setIframeHeight(nextHeight);
    setLoading(false);
  }, []);

  const doQuestionaireCompletion = useCallback(async (ticketInfo: Ticket) => {
    await completeQuestionnaire(ticketInfo.workOrder.appointment.id);

    const updatedTicket = await fetchTicket(ticketInfo.hashId);

    if (!updatedTicket) {
      return;
    }

    completeCurrentStep();

    if (!updatedTicket.workOrder.appointment.isSchedulable) {
      throwAsyncError(
        new SelfServiceError({
          reason: SupportedErrorTypes.appointmentNotPossible,
        })
      );
    } else {
      navigateToNextStep();
    }
  }, [completeCurrentStep, completeQuestionnaire, fetchTicket, navigateToNextStep, throwAsyncError]);

  const messageListener = useCallback(({ data }: MessageEvent<{ payload: string }>) => {
    if (!isSalesforceMessage(data)) {
      return;
    }

    cleanupIframeFailedToLoadTimeout();

    if (data.payload === 'QUESTIONNAIRE_COMPLETED' && ticket) {
      doQuestionaireCompletion(ticket);
    } else if (data.payload === 'QUESTIONNAIRE_SCROLL_TOP') {
      scrollTop();
    } else if (isInteger(data.payload) && data.payload > 0) {
      initializeIframe(data.payload);
    }
  }, [doQuestionaireCompletion, scrollTop, initializeIframe, ticket, cleanupIframeFailedToLoadTimeout]);

  useEffect(() => {
    const unsubscribe = QuestionsController.subscribeWindowMessages(messageListener);

    return unsubscribe;
  }, [messageListener]);

  const handleIframeLoad: ReactEventHandler<HTMLIFrameElement> = useCallback(() => {
    // set a timeout that if within 15 seconds if no message comes from salesforce
    // send an error event to backend
    iframeFailedToLoadTimeout.current = setTimeout(() => {
      sendEvents([{
        type: 'error',
        name: 'iframe-failed-to-load',
        message: 'Iframe failed to load',
      }]);
    }, 15000);
  }, [sendEvents]);

  const styles = classNames(style.ticketQuestions, {
    [style.justifyCenter]: (isPending || isLoading)
  });

  return (
    <div className={styles}>
      {(isPending || !isLoading) && <BackButton text={t('question:goBack')} />}
      {(isPending || isLoading) && (
        <div className={style.imagesContainer}>
          <LoadingImages
            items={isLoading
              ? [{ image: 'loadingQuestions', text: t('question:loadingQuestions'), ariaLabel: t('question:loadingQuestions') }]
              : isPending
                ? [{ image: 'questionsSaved', text: t('question:savingAnswers'), ariaLabel: t('question:savingAnswers') }]
                : []
            }
          />
        </div>
      )}
      {!!url && <iframe
        aria-label={t('stepsBar:questions')}
        height={iframeHeight}
        // onLoad is used for both success / error
        // onError is not called by some browsers thus not used
        onLoad={handleIframeLoad}
        src={url}
        title="questions"
      />}
    </div>
  );
};

export default Questions;
